import * as React from 'react';
import { RetsinfoHarvestRecord, RetsinfoRecordFlagValue } from '../../../../amorg-common';
import { articlesClientApi } from '../../../../api/articlesClientApi';
import { Flex } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { LoadingPageOverlay } from '../../../../designSystem/LoadingPageOverlay';
import { H1 } from '../../../../designSystem/typography/Heading';
import { toDateString } from '../../../../utils/date';
import { ArticlesTab } from '../_components/ArticlesTab';
import { AmorgyPageWrapper } from '../../../_pageWrappers/AmorgyPageWrapper';
import { MainRightTwoColumnLayout } from '../../../_layouts/TwoColumnLayouts';
import { SingleColumnLayout } from '../../../_layouts/SingleColumnLayout';
import { ButtonCalendar } from './_components/ButtonCalendar';
import { DateRecordPanel } from './_components/DateRecordPanel';
import { Checkbox } from '../../../../designSystem/inputs/Checkbox';
import { Span } from '../../../../designSystem/typography/Body';


export default function ArticlesRetsinfoOverviewPage (): JSX.Element 
{
    const [loading, setLoading] = React.useState<boolean>(true);
    const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
    const [records, setRecords] = React.useState<RetsinfoHarvestRecord[]>([]);
    const [showHidden, setShowHidden] = React.useState<boolean>(false);

    const [cooldown, setCooldown] = React.useState<number>(0);

    React.useEffect(() =>
    {
        document.title = 'Amorgy - Lovkrav retsinfo';
    }, []);

    React.useEffect(() =>
    {
        let cancelled = false;
        setLoading(true);
        setRecords([]);

        (async () =>
        {
            try
            {
                const records = await articlesClientApi.getRetsinfoRecords(selectedDate);
                if (!cancelled)
                    setRecords(records);
            }
            finally
            {
                if (!cancelled)
                    setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [selectedDate]);

    React.useEffect(() =>
    {
        if (cooldown > 0)
        {
            setTimeout(() =>
            {
                setCooldown(cooldown - 1);
            }, 1000);
        }
    }, [cooldown]);

    async function startCooldown ()
    {
        setCooldown(10);
    }

    async function handleClickFetch (date: Date): Promise<void>
    {
        setLoading(true);
        await articlesClientApi.fetchRetsinfoRecords(date, false);
        startCooldown();
        const records = await articlesClientApi.getRetsinfoRecords(selectedDate);
        setRecords(records);
        setLoading(false);
    }

    async function handleClickRefetch (date: Date): Promise<void>
    {
        setLoading(true);
        await articlesClientApi.fetchRetsinfoRecords(date, true);
        startCooldown();
        const records = await articlesClientApi.getRetsinfoRecords(selectedDate);
        setRecords(records);
        setLoading(false);
    }

    async function handleClickRefetchDocument (recordId: string, changeIndex: number): Promise<void>
    {
        if(!recordId) return;
        
        setLoading(true);
        await articlesClientApi.fetchRetsinfoRecordChangeDoc(recordId, changeIndex);
        const records = await articlesClientApi.getRetsinfoRecords(selectedDate);
        setRecords(records);
        setLoading(false);
    }

    async function handleClickSetFlag (id: string, changeIndex: number, flagValue: RetsinfoRecordFlagValue)
    {
        setLoading(true);
        await articlesClientApi.updateRetsinfoRecordFlag(id, changeIndex, flagValue);
        const updatedRecords = await articlesClientApi.getRetsinfoRecords(selectedDate);
        setRecords(updatedRecords);
        setLoading(false);
    }

    const selectedRecord = records.find(record =>
        record.recordYear === selectedDate.getFullYear() &&
        record.recordMonth === selectedDate.getMonth() + 1 &&
        record.recordDay === selectedDate.getDate()
    ) || null;

    return (
        <AmorgyPageWrapper selectedTab='articles'
            subMenuContent={
                <ArticlesTab selectedTab='retsinfo' />
            }>
            <LoadingPageOverlay loading={false} />
            <SingleColumnLayout>
            <MainRightTwoColumnLayout

                left={
                    <Flex flexDirection='column' gap={16}>
                        <ButtonCalendar
                            records={records}
                            selectedDate={selectedDate}
                            onSelectDate={setSelectedDate}
                            showHidden={showHidden}
                            disabled={loading}
                        />
                    </Flex>
                }

                main={

                    <Flex flexDirection='column' gap={16}>

                        <LoadingPageOverlay loading={loading} />

                        <H1>Retsinfo dataintegration</H1>
                        <Flex alignItems='center' gap={8}>
                            <Span>Vis skjulte ændringer</Span>
                            <Checkbox
                                checked={showHidden}
                                text={''} 
                                value={'show-hidden'}
                                onChange={(value, checked) => setShowHidden(checked)}  />
                        </Flex>

                        {!loading &&
                            <DateRecordPanel
                                key={toDateString(selectedDate)}
                                date={selectedDate}
                                cooldown={cooldown}
                                record={selectedRecord}
                                showHidden={showHidden}
                                onClickFetch={handleClickFetch}
                                onClickRefetch={handleClickRefetch}
                                onClickRefetchDocument={handleClickRefetchDocument}
                                onClickSetFlag={(changeIndex, flagValue) => handleClickSetFlag(selectedRecord?.id || '', changeIndex, flagValue)}
                            />
                        }

                    </Flex>
                } />
            </SingleColumnLayout>
        </AmorgyPageWrapper>
    );
}
